import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { UserProfile as UserProfileType, UserActivity, SocialPost, Comment, ProfileAnalytics, ProfileGallery, ProductCollectionEntry, ReviewPortfolioEntry, ProfileView } from '../types'
import FollowSystem from '../components/FollowSystem'
import ProfileCustomization from '../components/ProfileCustomization'
import ProfileAnalyticsDashboard from '../components/ProfileAnalyticsDashboard'
import ProfileGalleries from '../components/ProfileGalleries'
import ProfilePrivacyControls from '../components/ProfilePrivacyControls'
import VendorTypeBadges from '../components/VendorTypeBadges'

// Mock data for profile
const mockProfile: UserProfileType = {
  id: 'profile-1',
  userId: 'demo-user',
  displayName: 'Kratom Expert',
  bio: 'Been using kratom for 5+ years. Love sharing knowledge about strains and helping newcomers find what works for them. Always down to discuss experiences and vendors!',
  avatar: '🧑‍🔬',
  location: 'Denver, CO',
  joinDate: new Date('2019-03-15'),
  postCount: 847,
  commentCount: 2156,
  reputation: 4850,
  badges: [
    {
      id: 'badge-1',
      name: 'Veteran User',
      description: 'Member for over 3 years',
      icon: '🏆',
      dateEarned: new Date('2022-03-15'),
      rarity: 'rare'
    },
    {
      id: 'badge-2',
      name: 'Helpful Reviewer',
      description: 'Written 50+ helpful reviews',
      icon: '⭐',
      dateEarned: new Date('2023-01-20'),
      rarity: 'epic'
    },
    {
      id: 'badge-3',
      name: 'Community Leader',
      description: 'High reputation in community',
      icon: '👑',
      dateEarned: new Date('2023-06-10'),
      rarity: 'legendary'
    }
  ],
  followers: ['user1', 'user2', 'user3'],
  following: ['vendor1', 'moderator1'],
  isOnline: true,
  lastActive: new Date(),
  avatarType: 'emoji',
  customization: {
    theme: 'default',
    accentColor: '#00d166',
    layoutStyle: 'modern',
    showBadges: true,
    showStats: true,
    showActivity: true,
    featuredContent: []
  },
  analytics: {} as any,
  privacy: {
    profileVisibility: 'public',
    showLastActive: true,
    showLocation: true,
    showFollowerCount: true,
    showAnalytics: false,
    allowProfileIndexing: true,
    whoCanMessage: 'everyone',
    whoCanViewAnalytics: 'self',
    blockedUsers: [],
    restrictedViewers: []
  },
  galleries: [],
  productCollection: [],
  reviewPortfolio: [],
  profileViews: [],
  preferences: {
    showEmail: false,
    showLocation: true,
    allowMessages: true,
    emailNotifications: true,
    profileTheme: 'default',
    showAnalytics: false,
    trackVisitors: true
  }
}

const mockTrustedProfile: UserProfileType = {
  id: 'profile-trusted',
  userId: 'trusted-user',
  displayName: 'Strain Expert',
  bio: 'Long-time kratom enthusiast and community helper. Known for detailed strain reviews and helping newcomers navigate their kratom journey safely.',
  avatar: '🧙‍♂️',
  location: 'Austin, TX',
  joinDate: new Date('2020-05-20'),
  postCount: 567,
  commentCount: 1243,
  reputation: 2890,
  badges: [
    {
      id: 'badge-trusted-1',
      name: 'Trusted Member',
      description: 'Earned community trust through helpful contributions',
      icon: '⭐',
      dateEarned: new Date('2021-08-15'),
      rarity: 'rare'
    },
    {
      id: 'badge-trusted-2',
      name: 'Strain Specialist',
      description: 'Expert knowledge of kratom strains and effects',
      icon: '🌿',
      dateEarned: new Date('2022-03-10'),
      rarity: 'epic'
    }
  ],
  followers: ['user1', 'user2'],
  following: ['vendor1'],
  isOnline: true,
  lastActive: new Date(),
  preferences: {
    showEmail: false,
    showLocation: true,
    allowMessages: true,
    emailNotifications: true
  }
}


const mockVendor2Profile: UserProfileType = {
  id: 'profile-vendor2',
  userId: 'vendor2-user',
  displayName: 'Natural Botanicals',
  bio: 'Family-owned kratom business focused on sustainable sourcing and premium quality. We believe in transparency and customer education.',
  avatar: '🌱',
  location: 'Seattle, WA',
  joinDate: new Date('2019-03-10'),
  postCount: 156,
  commentCount: 389,
  reputation: 2234,
  badges: [
    {
      id: 'badge-vendor2-1',
      name: 'Verified Vendor',
      description: 'Officially verified kratom vendor',
      icon: '✅',
      dateEarned: new Date('2019-04-01'),
      rarity: 'epic'
    },
    {
      id: 'badge-vendor2-2',
      name: 'Sustainability Leader',
      description: 'Committed to sustainable kratom sourcing',
      icon: '🌍',
      dateEarned: new Date('2020-07-15'),
      rarity: 'rare'
    }
  ],
  followers: ['user1', 'user2', 'user3'],
  following: ['trusted-user'],
  vendorInfo: {
    companyName: 'Natural Botanicals',
    website: 'https://naturalbotanicals.com',
    description: 'Family-owned business specializing in ethically sourced, lab-tested kratom.',
    verificationDate: new Date('2019-04-01'),
    specialties: ['White Maeng Da', 'Green Thai', 'Red Bali', 'Gold Blends'],
    businessHours: 'Mon-Fri 8AM-5PM PST',
    contactEmail: 'info@naturalbotanicals.com',
    socialLinks: {
      reddit: 'https://reddit.com/u/naturalbotanicals',
      instagram: 'https://instagram.com/naturalbotanicals'
    }
  },
  isOnline: true,
  lastActive: new Date(),
  preferences: {
    showEmail: false,
    showLocation: true,
    allowMessages: true,
    emailNotifications: true
  }
}

const mockVendorProfile: UserProfileType = {
  id: 'profile-vendor',
  userId: 'kilt-user',
  displayName: 'Premium Kratom Co.',
  bio: 'Quality kratom vendor since 2018. We specialize in premium red and green strains with full lab testing. Customer satisfaction is our #1 priority!',
  avatar: '🏪',
  location: 'Portland, OR',
  joinDate: new Date('2018-08-12'),
  postCount: 234,
  commentCount: 567,
  reputation: 3450,
  badges: [
    {
      id: 'badge-vendor-1',
      name: 'Verified Vendor',
      description: 'Officially verified kratom vendor',
      icon: '✅',
      dateEarned: new Date('2018-09-01'),
      rarity: 'epic'
    },
    {
      id: 'badge-vendor-2',
      name: 'Quality Assured',
      description: 'Provides lab testing for all products',
      icon: '🔬',
      dateEarned: new Date('2019-02-15'),
      rarity: 'rare'
    }
  ],
  followers: ['user1', 'user2', 'user3', 'user4', 'user5'],
  following: ['user1', 'user2'],
  vendorInfo: {
    companyName: 'Premium Kratom Co.',
    website: 'https://premiumkratom.com',
    description: 'Premium quality kratom with full lab testing and customer satisfaction guarantee.',
    verificationDate: new Date('2018-09-01'),
    specialties: ['Red Maeng Da', 'Green Malay', '7OH Extracts', 'MIT Extracts'],
    businessHours: 'Mon-Fri 9AM-6PM PST',
    contactEmail: 'support@premiumkratom.com',
    socialLinks: {
      reddit: 'https://reddit.com/u/premiumkratom',
      facebook: 'https://facebook.com/premiumkratom',
      instagram: 'https://instagram.com/premiumkratom'
    }
  },
  isOnline: false,
  lastActive: new Date(Date.now() - 2 * 60 * 60 * 1000), // 2 hours ago
  preferences: {
    showEmail: false,
    showLocation: true,
    allowMessages: true,
    emailNotifications: true
  }
}

const mockActivity: UserActivity[] = [
  {
    id: 'activity-1',
    userId: 'demo-user',
    type: 'thread_created',
    description: 'Created thread "Best morning strains for energy?"',
    timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000),
    relatedId: 'thread-1',
    isPublic: true
  },
  {
    id: 'activity-2',
    userId: 'demo-user',
    type: 'comment_created',
    description: 'Commented on "Red Dragon effects review"',
    timestamp: new Date(Date.now() - 5 * 60 * 60 * 1000),
    relatedId: 'comment-1',
    isPublic: true
  },
  {
    id: 'activity-3',
    userId: 'demo-user',
    type: 'badge_earned',
    description: 'Earned the "Community Leader" badge',
    timestamp: new Date(Date.now() - 24 * 60 * 60 * 1000),
    relatedId: 'badge-3',
    isPublic: true
  },
  {
    id: 'activity-4',
    userId: 'demo-user',
    type: 'user_followed',
    description: 'Started following @PremiumKratom',
    timestamp: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000),
    relatedId: 'vendor1',
    isPublic: true
  }
]

const mockPosts: SocialPost[] = [
  {
    id: 'post-1',
    authorId: 'demo-user',
    authorUsername: 'demo',
    authorRole: 'member',
    type: 'experience_report',
    content: 'Just tried the new Red Maeng Da batch from @PremiumKratom and wow! Perfect for evening relaxation. Great quality as always. Anyone else tried this batch?',
    dateCreated: new Date(Date.now() - 4 * 60 * 60 * 1000),
    isDeleted: false,
    likes: 23,
    comments: 8,
    shares: 3,
    visibility: 'public',
    tags: ['red-maeng-da', 'experience', 'review'],
    extractInfo: {
      extractType: '7OH Tablets',
      vendor: 'Extract Supply Co.',
      dose: '10mg',
      effects: ['Relaxing', 'Pain Relief', 'Mood Boost'],
      rating: 5,
      notes: 'Perfect evening strain'
    }
  },
  {
    id: 'post-2',
    authorId: 'demo-user',
    authorUsername: 'demo',
    authorRole: 'member',
    type: 'text',
    content: 'Quick tip for newcomers: Start with smaller doses and work your way up. Better to take too little than too much! What advice would you give to someone just starting out?',
    dateCreated: new Date(Date.now() - 24 * 60 * 60 * 1000),
    isDeleted: false,
    likes: 45,
    comments: 12,
    shares: 7,
    visibility: 'public',
    tags: ['advice', 'newcomers', 'dosing']
  }
]

function formatTimestamp(date: Date): string {
  const now = new Date()
  const diff = now.getTime() - date.getTime()
  const minutes = Math.floor(diff / 60000)
  const hours = Math.floor(diff / 3600000)
  const days = Math.floor(diff / 86400000)

  if (minutes < 1) return 'Just now'
  if (minutes < 60) return `${minutes}m ago`
  if (hours < 24) return `${hours}h ago`
  if (days < 30) return `${days}d ago`
  return date.toLocaleDateString()
}

function getRoleColor(role: string): string {
  switch (role) {
    case 'admin': return '#e53e3e'
    case 'moderator': return '#9f7aea'
    case 'verified_vendor': return '#00d166'
    case 'trusted': return '#4299e1'
    default: return '#a0aec0'
  }
}

function getRoleBadge(role: string): string {
  switch (role) {
    case 'admin': return '👑'
    case 'moderator': return '🛡️'
    case 'verified_vendor': return '✅'
    case 'trusted': return '⭐'
    default: return ''
  }
}

function getBadgeColor(rarity: string): string {
  switch (rarity) {
    case 'legendary': return '#ffd700'
    case 'epic': return '#9f7aea'
    case 'rare': return '#4299e1'
    default: return '#68d391'
  }
}

// Mock enhanced profile data
const mockAnalytics: ProfileAnalytics = {
  totalViews: 12847,
  uniqueVisitors: 8932,
  viewsThisMonth: 1247,
  viewsThisWeek: 342,
  engagementRate: 0.076,
  topReferrers: [
    { source: 'direct', count: 4521 },
    { source: 'search', count: 3214 },
    { source: 'social', count: 2156 },
    { source: 'forum', count: 1890 },
    { source: 'review', count: 1066 }
  ],
  popularContent: [
    { id: 'post-1', type: 'post', views: 1247, title: 'Best morning kratom strains' },
    { id: 'review-3', type: 'review', views: 989, title: 'Green Malay vendor comparison' },
    { id: 'post-5', type: 'post', views: 756, title: 'Kratom dosage guide for beginners' }
  ],
  followerGrowth: [
    { date: new Date('2024-01-01'), count: 45 },
    { date: new Date('2024-02-01'), count: 67 },
    { date: new Date('2024-03-01'), count: 89 },
    { date: new Date('2024-04-01'), count: 112 },
    { date: new Date('2024-05-01'), count: 134 },
    { date: new Date('2024-06-01'), count: 156 }
  ],
  activityHeatmap: Array.from({ length: 365 }, (_, i) => ({
    date: new Date(Date.now() - i * 24 * 60 * 60 * 1000),
    activity: Math.floor(Math.random() * 10)
  })),
  demographics: {
    topLocations: [
      { location: 'United States', count: 5432 },
      { location: 'Canada', count: 1876 },
      { location: 'United Kingdom', count: 987 },
      { location: 'Australia', count: 654 },
      { location: 'Germany', count: 321 }
    ],
    userTypes: [
      { type: 'member', count: 6789 },
      { type: 'trusted', count: 1234 },
      { type: 'verified_vendor', count: 456 },
      { type: 'moderator', count: 123 }
    ]
  }
}

const mockGalleries: ProfileGallery[] = [
  {
    id: 'gallery-1',
    name: 'Photo Gallery',
    description: 'My kratom photos and experiences',
    type: 'photos',
    items: [
      {
        id: 'item-1',
        type: 'image',
        url: '/api/placeholder/300/200',
        title: 'Green Malay Powder',
        description: 'Fresh batch from Premium Vendor',
        dateAdded: new Date(),
        order: 1,
        tags: ['green', 'malay', 'energizing']
      },
      {
        id: 'item-2',
        type: 'image',
        url: '/api/placeholder/300/200',
        title: 'Red Bali Capsules',
        description: 'Evening relaxation strain',
        dateAdded: new Date(),
        order: 2,
        tags: ['red', 'bali', 'relaxing']
      }
    ],
    isPublic: true,
    createdDate: new Date(),
    lastUpdated: new Date(),
    viewCount: 234,
    order: 1
  },
  {
    id: 'gallery-2',
    name: 'Product Collection',
    description: 'My 7OH tabs, powders, and MIT extracts library',
    type: 'products',
    items: [],
    isPublic: true,
    createdDate: new Date(),
    lastUpdated: new Date(),
    viewCount: 156,
    order: 2
  },
  {
    id: 'gallery-3',
    name: 'Review Portfolio',
    description: 'My featured reviews and experiences',
    type: 'reviews',
    items: [],
    isPublic: true,
    createdDate: new Date(),
    lastUpdated: new Date(),
    viewCount: 89,
    order: 3
  }
]

const mockProductCollection: ProductCollectionEntry[] = [
  {
    id: 'product-1',
    productName: 'Premium 7OH Tablets',
    vendor: 'Elite Extracts',
    vendorId: 'elite-extracts',
    type: '7oh-tabs',
    potency: '10mg per tablet',
    rating: 5,
    notes: 'Incredibly potent and clean 7OH tablets. Perfect for pain relief and relaxation. Consistent dosing and lab tested.',
    effects: ['pain-relief', 'relaxation', 'euphoria', 'sleep-aid'],
    dateAdded: new Date('2024-01-15'),
    isFavorite: true,
    isWishlist: false,
    isDailyDriver: true,
    purchaseHistory: [
      { date: new Date('2024-01-15'), amount: '60 tablets', price: 120, batch: '7OH-T-001', potencyTested: '9.8mg per tab' },
      { date: new Date('2024-03-20'), amount: '120 tablets', price: 200, batch: '7OH-T-004', potencyTested: '10.2mg per tab' }
    ],
    images: ['https://images.unsplash.com/photo-1584308666744-24d5c474f2ae?w=800&h=600&fit=crop&crop=center'],
    tags: ['premium', 'lab-tested', 'consistent', 'favorite'],
    labResults: {
      '7oh': 10.2,
      mitragynine: 2.1,
      testDate: new Date('2024-03-15'),
      labName: 'Green Scientific Labs'
    }
  },
  {
    id: 'product-2',
    productName: 'Ultra 7OH Powder',
    vendor: 'Alkaloid Sciences',
    vendorId: 'vendor-2',
    type: '7oh-powder',
    potency: '5% 7OH powder',
    rating: 4,
    notes: 'High quality 7OH powder extract. Great for making custom doses. Bitter taste but very effective.',
    effects: ['potent-pain-relief', 'deep-relaxation', 'euphoria', 'sedating'],
    dateAdded: new Date('2024-02-10'),
    isFavorite: true,
    isWishlist: false,
    isDailyDriver: false,
    purchaseHistory: [
      { date: new Date('2024-02-10'), amount: '5g', price: 150, batch: '7OH-P-202', potencyTested: '4.8% 7OH' }
    ],
    images: [],
    tags: ['high-potency', 'powder', 'custom-dosing'],
    labResults: {
      '7oh': 48,
      mitragynine: 8.2,
      testDate: new Date('2024-02-05'),
      labName: 'Independent Testing Solutions'
    }
  },
  {
    id: 'product-3',
    productName: 'Pure MIT Extract',
    vendor: 'Extract Masters',
    vendorId: 'vendor-3',
    type: 'mit-extract',
    potency: '45% Mitragynine',
    rating: 4,
    notes: 'Clean mitragynine extract with good energy and mood boost. Less sedating than 7OH products.',
    effects: ['energy', 'mood-boost', 'focus', 'mild-pain-relief'],
    dateAdded: new Date('2024-03-05'),
    isFavorite: false,
    isWishlist: false,
    isDailyDriver: false,
    purchaseHistory: [
      { date: new Date('2024-03-05'), amount: '2g', price: 80, batch: 'MIT-E-445', potencyTested: '43.2% MIT' }
    ],
    images: [],
    tags: ['energizing', 'clean', 'daytime'],
    labResults: {
      '7oh': 0.8,
      mitragynine: 432,
      testDate: new Date('2024-03-01'),
      labName: 'Green Scientific Labs'
    }
  },
  {
    id: 'product-4',
    productName: 'Nano 7OH Tablets',
    vendor: 'Precision Alkaloids',
    vendorId: 'vendor-4',
    type: '7oh-tabs',
    potency: '5mg per tablet',
    rating: 5,
    notes: 'Perfect micro-dosing tablets. Great for functional pain relief without heavy sedation. Love the precise dosing.',
    effects: ['functional-pain-relief', 'mild-relaxation', 'mood-enhancement'],
    dateAdded: new Date('2024-04-01'),
    isFavorite: true,
    isWishlist: false,
    purchaseHistory: [
      { date: new Date('2024-04-01'), amount: '100 tablets', price: 150, batch: 'N7OH-500', potencyTested: '4.9mg per tab' }
    ],
    images: [],
    tags: ['micro-dose', 'functional', 'precise', 'favorite'],
    labResults: {
      '7oh': 4.9,
      mitragynine: 1.2,
      testDate: new Date('2024-03-28'),
      labName: 'Analytical Labs Inc'
    }
  },
  {
    id: 'product-5',
    productName: 'Full Spectrum Extract',
    vendor: 'Botanical Innovations',
    vendorId: 'vendor-5',
    type: 'mit-extract',
    potency: '35% MIT + 2% 7OH',
    rating: 3,
    notes: 'Interesting full spectrum extract. Good balance but not as potent as I hoped for the price.',
    effects: ['balanced', 'mild-euphoria', 'moderate-pain-relief'],
    dateAdded: new Date('2024-05-15'),
    isFavorite: false,
    isWishlist: false,
    isDailyDriver: false,
    purchaseHistory: [
      { date: new Date('2024-05-15'), amount: '3g', price: 120, batch: 'FS-EXT-201', potencyTested: '33.8% MIT, 1.8% 7OH' }
    ],
    images: [],
    tags: ['full-spectrum', 'expensive', 'okay'],
    labResults: {
      '7oh': 18,
      mitragynine: 338,
      testDate: new Date('2024-05-10'),
      labName: 'Independent Testing Solutions'
    }
  },
  {
    id: 'product-6',
    productName: 'Maximum Strength 7OH',
    vendor: 'Alpha Alkaloids',
    vendorId: 'vendor-6',
    type: '7oh-powder',
    potency: '7.5% 7OH powder',
    rating: 0,
    notes: 'Heard this is the strongest 7OH powder available. Expensive but want to try for severe pain days.',
    effects: ['maximum-pain-relief', 'heavy-sedation', 'intense-euphoria'],
    dateAdded: new Date('2024-06-01'),
    isFavorite: false,
    isWishlist: true,
    isDailyDriver: false,
    purchaseHistory: [],
    images: [],
    tags: ['wishlist', 'maximum-strength', 'expensive']
  },
  {
    id: 'product-7',
    productName: 'Rapid Release 7OH Caps',
    vendor: 'Fast Acting Botanicals',
    vendorId: 'vendor-7',
    type: '7oh-tabs',
    potency: '15mg per capsule',
    rating: 0,
    notes: 'Fast-acting capsule format. Curious about the rapid release technology.',
    effects: ['fast-acting', 'pain-relief', 'convenience'],
    dateAdded: new Date('2024-06-05'),
    isFavorite: false,
    isWishlist: true,
    isDailyDriver: false,
    purchaseHistory: [],
    images: [],
    tags: ['wishlist', 'fast-acting', 'convenient']
  }
]

const mockReviewPortfolio: ReviewPortfolioEntry[] = [
  {
    reviewId: 'review-1',
    vendorName: 'Elite Extracts',
    productName: 'Premium 7OH Tablets',
    productType: '7oh-tabs',
    potency: '10mg per tablet',
    rating: 5,
    dateWritten: new Date('2024-01-20'),
    helpfulVotes: 47,
    isFeatured: true,
    category: 'detailed',
    views: 1247,
    comments: 23
  },
  {
    reviewId: 'review-2',
    vendorName: 'Alkaloid Sciences',
    productName: 'Ultra 7OH Powder',
    productType: '7oh-powder',
    potency: '5% 7OH powder',
    rating: 4,
    dateWritten: new Date('2024-02-15'),
    helpfulVotes: 32,
    isFeatured: true,
    category: 'helpful',
    views: 896,
    comments: 18
  },
  {
    reviewId: 'review-3',
    vendorName: 'Extract Masters',
    productName: 'Pure MIT Extract',
    productType: 'mit-extract',
    potency: '45% Mitragynine',
    rating: 4,
    dateWritten: new Date('2024-03-10'),
    helpfulVotes: 28,
    isFeatured: false,
    category: 'recent',
    views: 543,
    comments: 12
  }
]

const mockProfileViews: ProfileView[] = [
  {
    id: 'view-1',
    viewerId: 'user-1',
    viewerUsername: 'KratomNewbie',
    timestamp: new Date(),
    source: 'direct',
    deviceType: 'desktop',
    duration: 180
  }
]

export default function UserProfile() {
  const { userId } = useParams<{ userId: string }>()
  const { user } = useAuth()
  const [activeTab, setActiveTab] = useState<'posts' | 'activity' | 'about' | 'galleries' | 'analytics' | 'settings'>('posts')
  const [isFollowing, setIsFollowing] = useState(false)
  const [profile, setProfile] = useState<UserProfileType | null>(null)
  const [isOwnProfile, setIsOwnProfile] = useState(false)
  const [showCustomization, setShowCustomization] = useState(false)

  useEffect(() => {
    // Simulate loading profile data
    // In real app, this would be an API call
    if (userId === 'kilt-user' || userId === 'kilt') {
      setProfile(mockVendorProfile)
    } else if (userId === 'trusted-user' || userId === 'trusteduser') {
      setProfile(mockTrustedProfile)
    } else if (userId === 'vendor2-user' || userId === 'botanicals') {
      setProfile(mockVendor2Profile)
    } else {
      setProfile(mockProfile)
    }
    
    setIsOwnProfile(userId === user?.id || (!userId && user))
    setIsFollowing(Math.random() > 0.5) // Random for demo
  }, [userId, user])

  if (!profile) {
    return (
      <div className="loading-screen">
        <div className="loading-content">
          <h2>Loading Profile...</h2>
        </div>
      </div>
    )
  }

  const handleFollow = () => {
    setIsFollowing(!isFollowing)
    // In real app, this would make an API call
  }

  // Get daily driver product for header background
  const dailyDriverProduct = mockProductCollection.find(p => p.isDailyDriver)

  return (
    <div className="user-profile-page">
      <div className="container">
        {/* Profile Header */}
        <div 
          className={`profile-header ${dailyDriverProduct ? 'has-daily-driver-bg' : ''}`}
          style={dailyDriverProduct ? {
            backgroundImage: `linear-gradient(rgba(45, 55, 72, 0.7), rgba(45, 55, 72, 0.7)), url(${dailyDriverProduct.images[0] || '/api/placeholder/800/300'})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat'
          } : {}}
        >
          <div className="profile-avatar">
            <span className="avatar-emoji">{profile.avatar}</span>
            {profile.isOnline && <span className="online-indicator">🟢</span>}
          </div>
          
          <div className="profile-info">
            <div className="profile-name-section">
              <h1 className="profile-name">
                {profile.displayName || `User ${profile.userId}`}
                {profile.vendorInfo && (
                  <span className="vendor-badge">✅ Verified Vendor</span>
                )}
              </h1>
              <p className="profile-username">@{profile.userId}</p>
              {profile.location && profile.preferences.showLocation && (
                <p className="profile-location">📍 {profile.location}</p>
              )}
            </div>

            <div className="profile-stats">
              <div className="stat">
                <span className="stat-number">{profile.postCount}</span>
                <span className="stat-label">Posts</span>
              </div>
              <div className="stat">
                <span className="stat-number">{profile.commentCount}</span>
                <span className="stat-label">Comments</span>
              </div>
              <div className="stat">
                <span className="stat-number">{profile.reputation}</span>
                <span className="stat-label">Reputation</span>
              </div>
            </div>


            <FollowSystem
              targetUserId={profile.userId}
              targetUsername={profile.displayName || profile.userId}
              initialFollowersCount={profile.followers.length}
              initialFollowingCount={profile.following.length}
              isFollowing={isFollowing}
              onFollowChange={setIsFollowing}
              showCounts={true}
              size="medium"
            />

            {!isOwnProfile && (
              <div className="profile-actions">
                <Link to={`/messages?user=${profile.userId}`} className="message-btn">
                  💬 Message
                </Link>
              </div>
            )}
          </div>

          {/* User Badges on right side */}
          <div className="header-badges">
            {profile.badges.slice(0, 3).map(badge => (
              <div key={badge.id} className="user-badge" title={badge.description}>
                <span className="badge-icon">{badge.icon}</span>
                <span className="badge-name">{badge.name}</span>
              </div>
            ))}
          </div>

          {/* Daily Driver Indicator - subtle corner badge */}
          {dailyDriverProduct && (
            <div className="daily-driver-corner-badge" title={`Daily Driver: ${dailyDriverProduct.productName} by ${dailyDriverProduct.vendor}`}>
              🏆
            </div>
          )}

          {/* Daily Driver Links */}
          {dailyDriverProduct && (
            <div className="daily-driver-links">
              <Link 
                to={`/vendor/${dailyDriverProduct.vendorId}`}
                className="daily-driver-vendor-link"
                title={`View ${dailyDriverProduct.vendor} vendor page`}
              >
                <span className="vendor-link-icon">🏪</span>
                <span className="vendor-link-text">Vendor</span>
              </Link>
              <Link 
                to={`/product/${dailyDriverProduct.id}`}
                className="daily-driver-product-link"
                title={`View ${dailyDriverProduct.productName} by ${dailyDriverProduct.vendor}`}
              >
                <span className="product-link-icon">🔗</span>
                <span className="product-link-text">Product</span>
              </Link>
            </div>
          )}
        </div>

        {/* Profile Bio */}
        {profile.bio && (
          <div className="profile-bio">
            <p>{profile.bio}</p>
          </div>
        )}

        {/* Vendor Information */}
        {profile.vendorInfo && (
          <div className="vendor-info-section">
            <h3>Vendor Information</h3>
            <div className="vendor-details">
              <div className="vendor-detail">
                <strong>Website:</strong> 
                <a href={profile.vendorInfo.website} target="_blank" rel="noopener noreferrer">
                  {profile.vendorInfo.website}
                </a>
              </div>
              <div className="vendor-detail">
                <strong>Specialties:</strong> {profile.vendorInfo.specialties.join(', ')}
              </div>
              <div className="vendor-detail">
                <strong>Business Hours:</strong> {profile.vendorInfo.businessHours}
              </div>
              <div className="vendor-detail">
                <strong>Verified Since:</strong> {profile.vendorInfo.verificationDate.toLocaleDateString()}
              </div>
            </div>
          </div>
        )}


        {/* Profile Tabs */}
        <div className="profile-tabs">
          <div className="tab-buttons">
            <button 
              className={`tab-btn ${activeTab === 'posts' ? 'active' : ''}`}
              onClick={() => setActiveTab('posts')}
            >
              Posts ({profile.postCount})
            </button>
            <button 
              className={`tab-btn ${activeTab === 'activity' ? 'active' : ''}`}
              onClick={() => setActiveTab('activity')}
            >
              Activity
            </button>
            <button 
              className={`tab-btn ${activeTab === 'about' ? 'active' : ''}`}
              onClick={() => setActiveTab('about')}
            >
              About
            </button>
            <button 
              className={`tab-btn ${activeTab === 'collection' ? 'active' : ''}`}
              onClick={() => setActiveTab('collection')}
            >
              💊 Product Collection
            </button>
            <button 
              className={`tab-btn ${activeTab === 'galleries' ? 'active' : ''}`}
              onClick={() => setActiveTab('galleries')}
            >
              🖼️ Galleries
            </button>
            {isOwnProfile && (
              <button 
                className={`tab-btn ${activeTab === 'analytics' ? 'active' : ''}`}
                onClick={() => setActiveTab('analytics')}
              >
                📊 Analytics
              </button>
            )}
            {isOwnProfile && (
              <button 
                className={`tab-btn ${activeTab === 'settings' ? 'active' : ''}`}
                onClick={() => setActiveTab('settings')}
              >
                ⚙️ Settings
              </button>
            )}
            {isOwnProfile && (
              <button 
                className="tab-btn customize-btn"
                onClick={() => setShowCustomization(true)}
              >
                🎨 Customize
              </button>
            )}
          </div>

          <div className="tab-content">
            {activeTab === 'posts' && (
              <div className="posts-tab">
                <div className="posts-list">
                  {mockPosts.map(post => (
                    <div key={post.id} className="post-card">
                      <div className="post-header">
                        <div className="post-author">
                          <span className="author-avatar">🧑‍🔬</span>
                          <div className="author-info">
                            <span 
                              className="author-name"
                              style={{ color: getRoleColor(post.authorRole) }}
                            >
                              {getRoleBadge(post.authorRole)} {post.authorUsername}
                            </span>
                            <span className="post-time">{formatTimestamp(post.dateCreated)}</span>
                          </div>
                        </div>
                        {post.type === 'experience_report' && (
                          <span className="post-type-badge">Experience Report</span>
                        )}
                      </div>

                      <div className="post-content">
                        <p>{post.content}</p>
                        
                        {post.extractInfo && (
                          <div className="strain-info">
                            <h4>Strain Details</h4>
                            <div className="strain-details">
                              <span><strong>Strain:</strong> {post.extractInfo.strain}</span>
                              <span><strong>Vendor:</strong> {post.extractInfo.vendor}</span>
                              <span><strong>Dose:</strong> {post.extractInfo.dose}</span>
                              <span><strong>Rating:</strong> {'⭐'.repeat(post.extractInfo.rating)}</span>
                            </div>
                            <div className="strain-effects">
                              <strong>Effects:</strong>
                              <div className="effects-tags">
                                {post.extractInfo.effects.map((effect, index) => (
                                  <span key={index} className="effect-tag">{effect}</span>
                                ))}
                              </div>
                            </div>
                          </div>
                        )}

                        {post.tags.length > 0 && (
                          <div className="post-tags">
                            {post.tags.map((tag, index) => (
                              <span key={index} className="post-tag">#{tag}</span>
                            ))}
                          </div>
                        )}
                      </div>

                      <div className="post-stats">
                        <span className="stat">❤️ {post.likes}</span>
                        <span className="stat">💬 {post.comments}</span>
                        <span className="stat">🔄 {post.shares}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'activity' && (
              <div className="activity-tab">
                <div className="activity-list">
                  {mockActivity.map(activity => (
                    <div key={activity.id} className="activity-item">
                      <div className="activity-icon">
                        {activity.type === 'thread_created' && '📝'}
                        {activity.type === 'comment_created' && '💬'}
                        {activity.type === 'badge_earned' && '🏆'}
                        {activity.type === 'user_followed' && '👤'}
                      </div>
                      <div className="activity-content">
                        <p className="activity-description">{activity.description}</p>
                        <span className="activity-time">{formatTimestamp(activity.timestamp)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'about' && (
              <div className="about-tab">
                <div className="about-info">
                  <div className="info-section">
                    <h4>Member Since</h4>
                    <p>{profile.joinDate.toLocaleDateString()}</p>
                  </div>
                  
                  <div className="info-section">
                    <h4>Last Active</h4>
                    <p>{formatTimestamp(profile.lastActive)}</p>
                  </div>

                  {profile.location && profile.preferences.showLocation && (
                    <div className="info-section">
                      <h4>Location</h4>
                      <p>📍 {profile.location}</p>
                    </div>
                  )}

                  <div className="info-section">
                    <h4>Community Reputation</h4>
                    <p>{profile.reputation} points</p>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'collection' && (
              <div className="collection-tab">
                <div className="collection-header">
                  <h3>💊 Product Collection</h3>
                  <div className="collection-stats">
                    <div className="stat-card">
                      <div className="stat-number">{mockProductCollection.length}</div>
                      <div className="stat-label">Total Products</div>
                    </div>
                    <div className="stat-card">
                      <div className="stat-number">{mockProductCollection.filter(p => p.isFavorite).length}</div>
                      <div className="stat-label">Favorites</div>
                    </div>
                    <div className="stat-card">
                      <div className="stat-number">{new Set(mockProductCollection.map(p => p.type)).size}</div>
                      <div className="stat-label">Product Types</div>
                    </div>
                  </div>
                </div>

                <div className="product-sections">
                  {/* 7OH Tablets Section */}
                  {mockProductCollection.filter(p => p.type === '7oh-tabs').length > 0 && (
                    <div className="product-section">
                      <h4>💊 7OH Tablets ({mockProductCollection.filter(p => p.type === '7oh-tabs').length})</h4>
                      <div className="product-grid">
                        {mockProductCollection.filter(p => p.type === '7oh-tabs').map(product => (
                          <div key={product.id} className={`product-card ${product.isFavorite ? 'favorite' : ''}`}>
                            <div className="product-header">
                              <h5>{product.productName}</h5>
                              <div className="product-rating">
                                {'★'.repeat(product.rating)}{'☆'.repeat(5-product.rating)}
                              </div>
                            </div>
                            <div className="product-vendor">{product.vendor}</div>
                            <div className="product-potency">{product.potency}</div>
                            <div className="product-effects">
                              {product.effects.slice(0, 3).map(effect => (
                                <span key={effect} className="effect-tag">{effect}</span>
                              ))}
                            </div>
                            {product.labResults && (
                              <div className="lab-results">
                                <div className="lab-badge">🧪 Lab Tested</div>
                                <div className="lab-info">
                                  7OH: {product.labResults['7oh']}%
                                </div>
                              </div>
                            )}
                            <div className="product-actions">
                              {product.isFavorite && (
                                <div className="favorite-badge">★ Favorite</div>
                              )}
                              {product.isDailyDriver && (
                                <div className="daily-driver-badge-small">🏆 Daily Driver</div>
                              )}
                              {isOwnProfile && !product.isDailyDriver && (
                                <button 
                                  className="set-daily-driver-btn"
                                  onClick={() => {
                                    // In real app, this would make an API call
                                    console.log(`Setting ${product.productName} as daily driver`)
                                  }}
                                  title="Set as Daily Driver"
                                >
                                  🏆 Set as Daily Driver
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* 7OH Powder Section */}
                  {mockProductCollection.filter(p => p.type === '7oh-powder').length > 0 && (
                    <div className="product-section">
                      <h4>🧪 7OH Powder ({mockProductCollection.filter(p => p.type === '7oh-powder').length})</h4>
                      <div className="product-grid">
                        {mockProductCollection.filter(p => p.type === '7oh-powder').map(product => (
                          <div key={product.id} className={`product-card ${product.isFavorite ? 'favorite' : ''}`}>
                            <div className="product-header">
                              <h5>{product.productName}</h5>
                              <div className="product-rating">
                                {'★'.repeat(product.rating)}{'☆'.repeat(5-product.rating)}
                              </div>
                            </div>
                            <div className="product-vendor">{product.vendor}</div>
                            <div className="product-potency">{product.potency}</div>
                            <div className="product-effects">
                              {product.effects.slice(0, 3).map(effect => (
                                <span key={effect} className="effect-tag">{effect}</span>
                              ))}
                            </div>
                            {product.labResults && (
                              <div className="lab-results">
                                <div className="lab-badge">🧪 Lab Tested</div>
                                <div className="lab-info">
                                  7OH: {product.labResults['7oh']}%
                                </div>
                              </div>
                            )}
                            <div className="product-actions">
                              {product.isFavorite && (
                                <div className="favorite-badge">★ Favorite</div>
                              )}
                              {product.isDailyDriver && (
                                <div className="daily-driver-badge-small">🏆 Daily Driver</div>
                              )}
                              {isOwnProfile && !product.isDailyDriver && (
                                <button 
                                  className="set-daily-driver-btn"
                                  onClick={() => {
                                    // In real app, this would make an API call
                                    console.log(`Setting ${product.productName} as daily driver`)
                                  }}
                                  title="Set as Daily Driver"
                                >
                                  🏆 Set as Daily Driver
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* MIT Extract Section */}
                  {mockProductCollection.filter(p => p.type === 'mit-extract').length > 0 && (
                    <div className="product-section">
                      <h4>⚗️ MIT Extract ({mockProductCollection.filter(p => p.type === 'mit-extract').length})</h4>
                      <div className="product-grid">
                        {mockProductCollection.filter(p => p.type === 'mit-extract').map(product => (
                          <div key={product.id} className={`product-card ${product.isFavorite ? 'favorite' : ''}`}>
                            <div className="product-header">
                              <h5>{product.productName}</h5>
                              <div className="product-rating">
                                {'★'.repeat(product.rating)}{'☆'.repeat(5-product.rating)}
                              </div>
                            </div>
                            <div className="product-vendor">{product.vendor}</div>
                            <div className="product-potency">{product.potency}</div>
                            <div className="product-effects">
                              {product.effects.slice(0, 3).map(effect => (
                                <span key={effect} className="effect-tag">{effect}</span>
                              ))}
                            </div>
                            {product.labResults && (
                              <div className="lab-results">
                                <div className="lab-badge">🧪 Lab Tested</div>
                                <div className="lab-info">
                                  MIT: {product.labResults.mitragynine}%
                                </div>
                              </div>
                            )}
                            <div className="product-actions">
                              {product.isFavorite && (
                                <div className="favorite-badge">★ Favorite</div>
                              )}
                              {product.isDailyDriver && (
                                <div className="daily-driver-badge-small">🏆 Daily Driver</div>
                              )}
                              {isOwnProfile && !product.isDailyDriver && (
                                <button 
                                  className="set-daily-driver-btn"
                                  onClick={() => {
                                    // In real app, this would make an API call
                                    console.log(`Setting ${product.productName} as daily driver`)
                                  }}
                                  title="Set as Daily Driver"
                                >
                                  🏆 Set as Daily Driver
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {mockProductCollection.length === 0 && (
                    <div className="no-products">
                      <h4>No Products Yet</h4>
                      <p>Start building your collection by adding your favorite 7OH and MIT products!</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'galleries' && (
              <div className="galleries-tab">
                <div className="tab-section">
                  <ProfileGalleries
                    galleries={mockGalleries}
                    productCollection={mockProductCollection}
                    reviewPortfolio={mockReviewPortfolio}
                    isOwnProfile={isOwnProfile}
                    onUpdateGallery={(galleryId, updates) => {
                      console.log('Update gallery:', galleryId, updates)
                    }}
                    onCreateGallery={(gallery) => {
                      console.log('Create gallery:', gallery)
                    }}
                    onDeleteGallery={(galleryId) => {
                      console.log('Delete gallery:', galleryId)
                    }}
                  />
                </div>
              </div>
            )}

            {activeTab === 'analytics' && isOwnProfile && (
              <ProfileAnalyticsDashboard
                profile={profile}
                analytics={mockAnalytics}
                isOwnProfile={isOwnProfile}
              />
            )}

            {activeTab === 'settings' && isOwnProfile && (
              <ProfilePrivacyControls
                profile={profile}
                privacy={{
                  profileVisibility: 'public',
                  showLastActive: true,
                  showLocation: true,
                  showFollowerCount: true,
                  showAnalytics: false,
                  allowProfileIndexing: true,
                  whoCanMessage: 'everyone',
                  whoCanViewAnalytics: 'self',
                  blockedUsers: [],
                  restrictedViewers: []
                }}
                profileViews={mockProfileViews}
                onUpdatePrivacy={(privacy) => {
                  console.log('Update privacy:', privacy)
                }}
              />
            )}
          </div>
        </div>

        {/* Profile Customization Modal */}
        {showCustomization && (
          <ProfileCustomization
            profile={profile}
            onSave={(updates) => {
              console.log('Save customization:', updates)
              setShowCustomization(false)
            }}
            onCancel={() => setShowCustomization(false)}
          />
        )}
      </div>
    </div>
  )
}